/**
 * Vue d'ensemble multi-projets (plan 2026-09-27) : calendrier global, charge des membres,
 * indicateurs de réussite. Trois onglets pilotés par `?vue=` (`lib/overview-url.js`), tout
 * l'état vit dans l'URL, rien en `useState` local pour les filtres — sauf le message de garde
 * du filtre période, qui n'est jamais qu'un retour de validation avant écriture dans l'URL.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LIMITS } from '@exiplan/shared-types';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { useOverview } from '../hooks/useOverview.js';
import { readOverviewParams, writeOverviewParams } from '../lib/overview-url.js';
import { daysBetween, shiftAnchor, todayISO } from '../lib/calendar.js';
import { PageBody, PageHeader } from '../components/common.jsx';
import { OverviewCalendar } from '../views/overview/OverviewCalendar.jsx';
import { OverviewWorkload } from '../views/overview/OverviewWorkload.jsx';
import { OverviewIndicators } from '../views/overview/OverviewIndicators.jsx';
import { FORM_FIELD, FORM_LABEL, INPUT_SM, SELECT_SM, TAB_ACTIVE, TAB_IDLE } from '../ui/theme.js';

const TAB_KEYS = [
  { key: 'calendrier', labelKey: 'overview.tabs.calendar' },
  { key: 'charge', labelKey: 'overview.tabs.workload' },
  { key: 'indicateurs', labelKey: 'overview.tabs.indicators' },
];

export function OverviewPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language?.split('-')[0];
  const today = useMemo(() => todayISO(), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = readOverviewParams(searchParams, today);
  const [periodError, setPeriodError] = useState(null);

  const patch = useCallback(
    (fields) => setSearchParams(writeOverviewParams(searchParams, fields), { replace: true }),
    [searchParams, setSearchParams],
  );

  const need = params.vue === 'calendrier' ? 'tasks' : 'insights';
  const { data, error, loading } = useOverview({
    from: params.from,
    to: params.to,
    teamId: params.teamId,
    projectId: params.projectId,
    assigneeId: params.memberId,
    need,
  });

  // Périmètre des listes Équipe/Projet/Membre : uniquement filtré par équipe, jamais par projet
  // ni membre — sinon choisir un projet réduisait la liste des projets à ce seul projet, et
  // choisir un membre à ce seul membre (issue « options des filtres qui se referment »).
  const { data: scopeData } = useOverview({
    from: params.from,
    to: params.to,
    teamId: params.teamId,
    need: 'tasks',
  });

  const { data: teamsData } = useAsync((signal) => fetchAllPages('/teams', { signal }), []);
  const teams = teamsData?.data ?? [];

  const projects = scopeData?.projects ?? [];
  const members = useMemo(() => {
    const byId = new Map();
    for (const task of scopeData?.tasks ?? []) {
      if (task.assigneeId && !byId.has(task.assigneeId)) {
        byId.set(task.assigneeId, { id: task.assigneeId, name: task.assigneeName });
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [scopeData]);

  const TABS = TAB_KEYS.map((tab) => ({ key: tab.key, label: t(tab.labelKey) }));
  const notAccessible = error?.status === 404;
  const otherError = Boolean(error) && !notAccessible;

  /** Changer d'équipe invalide le projet et le membre choisis (§ « équipe qui ne vide pas »). */
  function onTeamChange(value) {
    setPeriodError(null);
    patch({ equipe: value, projet: '', membre: '' });
  }

  /**
   * Garde client sur le filtre période (Charge/Indicateurs) : la fenêtre ne s'écrit dans l'URL
   * que si `a ≥ de` et sa durée ne dépasse pas `LIMITS.portfolioMaxDays` — même règle que la
   * validation Zod serveur, appliquée ici pour éviter un appel voué à un 400.
   */
  function onPeriodChange(field, value) {
    if (!value) return;
    const candidate = { from: params.from, to: params.to, [field]: value };
    if (candidate.to < candidate.from) {
      setPeriodError(t('overview.filters.periodOrderError'));
      return;
    }
    if (daysBetween(candidate.from, candidate.to) + 1 > LIMITS.portfolioMaxDays) {
      setPeriodError(t('overview.filters.periodRangeError', { days: LIMITS.portfolioMaxDays }));
      return;
    }
    setPeriodError(null);
    patch({ de: candidate.from, a: candidate.to });
  }

  return (
    <div>
      <PageHeader
        eyebrow={t('overview.eyebrow')}
        title={t('overview.title')}
        subtitle={t('overview.subtitle')}
      />

      <div className="border-b-2 border-line">
        <PageBody className="flex flex-wrap items-end gap-2 px-4 py-3">
          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('overview.filters.team')}</span>
            <select
              className={SELECT_SM}
              value={params.teamId ?? ''}
              onChange={(event) => onTeamChange(event.target.value)}
            >
              <option value="">{t('overview.filters.allTeams')}</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>

          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('overview.filters.project')}</span>
            <select
              className={SELECT_SM}
              value={params.projectId ?? ''}
              onChange={(event) => patch({ projet: event.target.value })}
            >
              <option value="">{t('overview.filters.allProjects')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.key} · {project.name}
                </option>
              ))}
            </select>
          </label>

          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('overview.filters.member')}</span>
            <select
              className={SELECT_SM}
              value={params.memberId ?? ''}
              onChange={(event) => patch({ membre: event.target.value })}
            >
              <option value="">{t('overview.filters.allMembers')}</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>

          {params.vue !== 'calendrier' && (
            <label className={FORM_FIELD}>
              <span className={FORM_LABEL}>{t('overview.filters.periodFrom')}</span>
              <input
                type="date"
                className={INPUT_SM}
                value={params.from}
                onChange={(event) => onPeriodChange('from', event.target.value)}
              />
            </label>
          )}
          {params.vue !== 'calendrier' && (
            <label className={FORM_FIELD}>
              <span className={FORM_LABEL}>{t('overview.filters.periodTo')}</span>
              <input
                type="date"
                className={INPUT_SM}
                value={params.to}
                onChange={(event) => onPeriodChange('to', event.target.value)}
              />
            </label>
          )}

          {params.vue === 'calendrier' && (
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                className={params.mode === 'mois' ? TAB_ACTIVE : TAB_IDLE}
                aria-current={params.mode === 'mois' ? 'page' : undefined}
                onClick={() => patch({ mode: 'mois' })}
              >
                {t('overview.mode.month')}
              </button>
              <button
                type="button"
                className={params.mode === 'semaine' ? TAB_ACTIVE : TAB_IDLE}
                aria-current={params.mode === 'semaine' ? 'page' : undefined}
                onClick={() => patch({ mode: 'semaine' })}
              >
                {t('overview.mode.week')}
              </button>
            </div>
          )}
        </PageBody>
        {periodError && (
          <PageBody className="px-4 pb-3">
            <p role="alert" className="text-sm text-statut-erreur">
              {periodError}
            </p>
          </PageBody>
        )}
      </div>

      <nav
        className="flex items-end gap-1 border-b-2 border-line px-4 pt-2"
        aria-label={t('overview.title')}
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={params.vue === tab.key ? TAB_ACTIVE : TAB_IDLE}
            aria-current={params.vue === tab.key ? 'page' : undefined}
            onClick={() => patch({ vue: tab.key })}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <PageBody className="p-4">
        {notAccessible ? (
          <p className="text-sm text-accent-ink">{t('overview.notAccessible')}</p>
        ) : otherError ? (
          <div
            role="alert"
            className="flex items-center gap-2 border-2 border-statut-erreur bg-statut-erreur-wash px-3 py-2 text-sm text-statut-erreur"
          >
            <span aria-hidden="true">▲</span>
            <p>{t('overview.loadError')}</p>
          </div>
        ) : loading && !data ? (
          <p className="text-sm text-ink-soft">{t('overview.loading')}</p>
        ) : (
          <>
            {data?.truncated && (
              <div
                role="alert"
                className="mb-3 flex items-center gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
              >
                <span aria-hidden="true">▲</span>
                <p>{t('overview.truncated')}</p>
              </div>
            )}
            {params.vue === 'calendrier' && (
              <OverviewCalendar
                data={data}
                today={data?.today ?? today}
                mode={params.mode}
                anchor={params.anchor}
                onShift={(delta) =>
                  patch({ ancre: shiftAnchor(params.anchor, params.mode, delta) })
                }
                locale={locale}
              />
            )}
            {params.vue === 'charge' && <OverviewWorkload data={data} memberId={params.memberId} />}
            {params.vue === 'indicateurs' && <OverviewIndicators data={data} />}
          </>
        )}
      </PageBody>
    </div>
  );
}
